import React from "react";
import { Button, Checkbox, Input, Select, Tag, withTranslation } from "@familyzone/component-library";
import { Box, Flex, FormControl, FormErrorMessage, FormLabel } from "@chakra-ui/react";
import CardBasedPage from "../templates/CardBasedPage";
import SectionHeading from "../header/components/SectionHeading";
import Api from "../../utils/Api";

import { validateContentHubUrl } from "../../utils/WebsiteUtil";
import { useCommunityFeatureFlagStore } from "../../storez/CommunityFeatureFlagStore";
import { useSessionStore } from "../../storez/SessionStore";
import { useDevicesStore } from "../../storez/DevicesStore";
import { useDeviceOptionsStore } from "../../storez/DeviceOptionsStore";
import { useFeatureFlagStore } from "../../storez/FeatureFlagStore";
import { withToast } from "../../utils/ReactUtil";

/**
 * TODO: Convert to Typescript + Functional Component
 */
class ManageDeviceSettings extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      deviceid: "",
      devices: [],
      key: "",
      user_defined_name: "",
      display_name: "",
      content_hub_url: "",
      timezone: "",
      timezones: [],
      all_timezones: [],
      cpu_info: {},
      dmi_info: {},
      uncl_serial: "",

      isSupportAdmin: useSessionStore.getState().supportAdmin,
      loading: true,
      saving: false,
      changes: false,
      validUrl: true,
      validDisplayName: true,

      isParentDevice: false,
      parentDevice: "",
      enableExtensionChromebooksOnly: false,
      studentAutoEnrolment: false,
      customer_internal_company: "",
      hubspot_company_id: "",
    };

    this.customer_internal_company_mapping = [
      { value: "linewize", text: "Linewize" },
      { value: "smoothwall", text: "Smoothwall" },
    ];
  }

  async componentDidMount() {
    const self = this;
    const promises = [
      Api.getAsync("/managedevice/ajax/device/timezones").then((result) => {
        self.setState({ timezones: result["result"] });
      }),
      Api.getAsync("/managedevice/ajax/device/all-timezones").then((result) => {
        self.setState({ all_timezones: result["result"] });
      }),
      useDeviceOptionsStore.getState().fetch().then(this.onDeviceOptionsLoaded),
      useCommunityFeatureFlagStore.getState().getOrFetch(),
    ];

    if (this.state.isSupportAdmin) {
      promises.push(useFeatureFlagStore.getState().fetch().then(this.onFeatureFlagsLoaded));
      if (useDevicesStore.getState().loaded) {
        this.setState({ devices: useDevicesStore.getState().devices });
      } else {
        promises.push(
          useDevicesStore
            .getState()
            .fetch()
            .then((devices) => self.setState({ devices }))
        );
      }

      // not awaited as it's not critical for the page
      Api.getAsync("/info").then(
        (result) => {
          self.setState({
            cpu_info: result?.device_information?.cpu_info || {},
            dmi_info: result?.device_information?.dmi_info || {},
          });
        },
        (err) => console.error(err)
      );
    }

    Promise.all(promises).then(
      () => {
        self.setState({ loading: false });
        if (this.state.isSupportAdmin) {
          self.onBothDeviceAndFlagsLoaded();
        }
      },
      (err) => {
        self.props.errorToast({
          title: self.props.t("Please try again"),
          description:
            err?.message && err?.status
              ? self.props.t(err.message)
              : self.props.t("Failed to load device configuration. If the issue persists, please contact support."),
          isClosable: true,
        });
      }
    );
  }

  onDeviceOptionsLoaded = (options) => {
    this.setState({
      deviceid: options.deviceid,
      uncl_serial: options.uncl_serial,
      key: options.key,
      user_defined_name: options.user_defined_name,
      display_name: options.display_name,
      content_hub_url: options.content_hub_url,
      timezone: options.timezone,
      parentDevice: options.parent_id,
      isParentDevice: options.is_parent,
      enableExtensionChromebooksOnly: options.enable_extension_chromebooks_only,
      studentAutoEnrolment: options.student_auto_enrolment,
      customer_internal_company: options.customer_internal_company,
      hubspot_company_id: options.hubspot_company_id,
    });
  };

  onFeatureFlagsLoaded = (flags) => {
    this.setState({
      featureFlags: flags,
    });
  };

  onBothDeviceAndFlagsLoaded = () => {
    const current_flag_state = this.state.customer_internal_company === this.customer_internal_company_mapping[1].value ? true : false;
    if (
      this.state.featureFlags &&
      (current_flag_state !== this.state.featureFlags.enable_ctiru_filter_lists ||
        current_flag_state !== this.state.featureFlags.enable_iwf_filter_lists)
    ) {
      this.setState({
        featureFlags: {
          ...this.state.featureFlags,
          enable_ctiru_filter_lists: current_flag_state,
          enable_iwf_filter_lists: current_flag_state,
        },
        changes: true,
      });
    }
  };

  handle_Submit = () => {
    if (this.state.changes) {
      this.setState({
        saving: true,
        changes: false,
      });

      let settings = {
        key: this.state.key,
        user_defined_name: this.state.user_defined_name,
        display_name: this.state.display_name,
        content_hub_url: this.state.content_hub_url,
        timezone: this.state.timezone,
        update_allow_auto: this.state.update_allow_auto,
        is_parent: this.state.isParentDevice ?? false,
        parent_id: this.state.parentDevice ?? "",
        enable_extension_chromebooks_only: this.state.enableExtensionChromebooksOnly,
        student_auto_enrolment: this.state.studentAutoEnrolment,
        customer_internal_company: this.state.customer_internal_company,
        hubspot_company_id: this.state.hubspot_company_id,
      };

      const promises = [];

      let self = this;
      promises.push(
        useDeviceOptionsStore
          .getState()
          .save(settings)
          .then(
            () => {
              useSessionStore.setState((state) => ({
                ...state,
                device: {
                  ...state.device,
                  name: settings.user_defined_name,
                  timezone: settings.timezone,
                },
              }));
            },
            (err) => {
              this.props.errorToast({
                title: this.props.t("Please try again"),
                description: this.props.t(err.message),
                isClosable: true,
              });
            }
          )
      );
      if (this.state.isSupportAdmin) {
        promises.push(
          useFeatureFlagStore
            .getState()
            .save(this.state.featureFlags)
            .catch((err) => {
              this.props.errorToast({
                title: this.props.t("Please try again"),
                description: this.props.t(err.message),
                isClosable: true,
              });
            })
        );
      }
      Promise.all(promises).then(() => self.setState({ saving: false }));
    }
  };

  handle_ChangeKey = (event) => {
    this.setState({
      key: event.target.value,
      changes: true,
    });
  };

  handle_ChangeName = (event) => {
    this.setState({
      user_defined_name: event.target.value,
      changes: true,
    });
  };

  handle_ChangeDisplayName = (event) => {
    const newName = event.target.value;

    // School name is required when Community is enabled; otherwise, optional
    const validDisplayName = useCommunityFeatureFlagStore.getState().flags?.enableCommunity ? !!newName : true;

    this.setState({
      display_name: newName,
      validDisplayName: validDisplayName,
      changes: true,
    });
  };

  handle_ChangeContentHubUrl = (event) => {
    this.setState({
      content_hub_url: event.target.value,
      validUrl: validateContentHubUrl(event.target.value),
      changes: true,
    });
  };

  handle_ChangeHubspotCompanyId = (event) => {
    if (event.target.value.trim() === "") {
      return;
    }
    const current_flag_state = this.state.customer_internal_company === this.customer_internal_company_mapping[1].value ? true : false;

    this.setState({
      hubspot_company_id: event.target.value,
      featureFlags: {
        ...this.state.featureFlags,
        enable_ctiru_filter_lists: current_flag_state,
        enable_iwf_filter_lists: current_flag_state,
      },
      changes: true,
    });
  };

  handle_ChangeTimezone = (event) => {
    this.setState({
      timezone: event.value,
      changes: true,
    });
  };

  get_SelectionMap = (current_timezone) => {
    let ret = [];
    for (let timezone of this.state.timezones) {
      ret.push({ value: timezone });
    }
    if (current_timezone && !this.state.timezones.includes(current_timezone)) {
      ret.push({ value: current_timezone });
    }
    return ret;
  };

  handle_ChangeIsParent = (event) => {
    this.setState({
      isParentDevice: event.target.checked,
      changes: true,
    });
  };

  handle_ChangeEnableExtensionChromebooksOnly = (event) => {
    this.setState({
      enableExtensionChromebooksOnly: event.target.checked,
      changes: true,
    });
  };

  handle_ChangeStudentAutoEnrolment = (event) => {
    this.setState({
      studentAutoEnrolment: event.target.checked,
      changes: true,
    });
  };

  handle_changeParentDevice = (event) => {
    this.setState({
      parentDevice: event.value,
      changes: true,
    });
  };

  handle_ChangeCustomerType = (entry) => {
    const current_flag_state = entry.value === this.customer_internal_company_mapping[1].value ? true : false;
    this.setState({
      customer_internal_company: entry.value,
      featureFlags: {
        ...this.state.featureFlags,
        enable_ctiru_filter_lists: current_flag_state,
        enable_iwf_filter_lists: current_flag_state,
      },
      changes: true,
    });
  };

  get_DeviceSelectionMap() {
    let ret = [{ value: "", text: "None" }];
    for (let device of this.state.devices) {
      if (this.state.deviceid !== device.deviceid) {
        ret.push({
          value: device.deviceid,
          text: device.device.user_defined_name ? `${device.device.user_defined_name} (${device.deviceid})` : device.deviceid,
        });
      }
    }
    return ret;
  }

  singleSelectorCss() {
    // stretch Select component like regular Input elements for consistent look
    return { "div, button, input": { width: "100%", textAlign: "left" } };
  }

  supportAdminTag() {
    return (
      <Box my="-3px" aria-hidden="true">
        <Tag size="compact" variant="orange" tagLabel={this.props.t("Only Visible to Support Admins")} />
      </Box>
    );
  }

  hasValidTimezone() {
    return this.state.loading || (this.state.timezone && this.state.all_timezones?.includes(this.state.timezone));
  }

  isDisabled() {
    return this.state.loading || this.state.saving;
  }

  canSave() {
    return !this.isDisabled() && this.state.changes && this.state.validUrl && this.state.validDisplayName && this.hasValidTimezone();
  }

  render() {
    return (
      <CardBasedPage
        title={this.props.t("Device Options")}
        breadcrumbs={[
          { title: this.props.t("Debugging"), url: "/managedevice", isActive: false },
          { title: this.props.t("Device Options"), isActive: true },
        ]}
      >
        <Box minHeight="200px" p="sp24" position="relative">
          <Flex direction="column" gap="sp16">
            <Box>
              <SectionHeading title={this.props.t("Device Configuration")} />

              <Flex px="sp12" py="sp16" direction="column" gap="sp16" maxWidth="776px">
                <FormControl isDisabled={this.isDisabled()}>
                  <Flex gap="sp8" alignItems="center">
                    <FormLabel width="100px">{this.props.t("Device ID")}</FormLabel>
                    <Box flexGrow={1}>
                      <Input name="deviceId" value={this.state.deviceid} isDisabled={true} />
                    </Box>
                  </Flex>
                </FormControl>

                {this.state.uncl_serial && (
                  <FormControl isDisabled={this.isDisabled()}>
                    <Flex gap="sp8" alignItems="center">
                      <FormLabel width="100px">{this.props.t("UNCL")}</FormLabel>
                      <Box flexGrow={1}>
                        <Input name="deviceUncl" value={this.state.uncl_serial} isDisabled={true} />
                      </Box>
                    </Flex>
                  </FormControl>
                )}

                <FormControl isDisabled={this.isDisabled()}>
                  <Flex gap="sp8" alignItems="center">
                    <FormLabel width="100px">{this.props.t("Device Key")}</FormLabel>
                    <Box flexGrow={1}>
                      <Input name="deviceKey" value={this.state.key} onChange={this.handle_ChangeKey} isDisabled={this.isDisabled()} />
                    </Box>
                  </Flex>
                </FormControl>

                <FormControl isDisabled={this.isDisabled()}>
                  <Flex gap="sp8" alignItems="center">
                    <FormLabel width="100px">{this.props.t("Device Name")}</FormLabel>
                    <Box flexGrow={1}>
                      <Input
                        name="deviceName"
                        value={this.state.user_defined_name}
                        onChange={this.handle_ChangeName}
                        isDisabled={this.isDisabled()}
                      />
                    </Box>
                  </Flex>
                </FormControl>

                <FormControl isDisabled={this.isDisabled()} isInvalid={!this.state.validDisplayName}>
                  <Flex gap="sp8" alignItems="center">
                    <FormLabel width="100px">{this.props.t("Organisation Name")}</FormLabel>
                    <Box flexGrow={1}>
                      <Input
                        name="organisationName"
                        value={this.state.display_name}
                        onChange={this.handle_ChangeDisplayName}
                        isDisabled={this.isDisabled()}
                        isInvalid={!this.state.validDisplayName}
                      />
                      <FormErrorMessage fontWeight="regular">
                        {this.props.t("Organisation name can't be empty because Linewize Parent is enabled")}
                      </FormErrorMessage>
                    </Box>
                  </Flex>
                </FormControl>

                <FormControl isDisabled={this.isDisabled()} isInvalid={!this.state.validUrl}>
                  <Flex gap="sp8" alignItems="center">
                    <FormLabel width="100px">{this.props.t("Content Hub")}</FormLabel>
                    <Box flexGrow={1}>
                      <Input
                        name="contentHubUrl"
                        value={this.state.content_hub_url}
                        onChange={this.handle_ChangeContentHubUrl}
                        isDisabled={this.isDisabled()}
                        isInvalid={!this.state.validUrl}
                      />
                      <FormErrorMessage fontWeight="regular">
                        {this.props.t("Please enter a valid URL starting with http:// or https:// (e.g., https://example.com).")}
                      </FormErrorMessage>
                    </Box>
                  </Flex>
                </FormControl>

                <FormControl isDisabled={this.isDisabled()} isInvalid={!this.hasValidTimezone()} isRequired={true}>
                  <Flex gap="sp8" alignItems="center">
                    <FormLabel width="100px" htmlFor="deviceTimezone-input">
                      {this.props.t("Timezone")}
                    </FormLabel>
                    <Box flexGrow={1} css={this.singleSelectorCss()}>
                      <Select
                        id="deviceTimezone"
                        placeholder={this.props.t("Select a timezone...")}
                        items={this.get_SelectionMap(this.state.timezone)}
                        selectedItem={
                          this.get_SelectionMap(this.state.timezone).find((o) => o.value === this.state.timezone) ?? { value: "" }
                        }
                        onChange={this.handle_ChangeTimezone}
                        disabled={this.isDisabled()}
                        required={true}
                        isFiltered={true}
                        isInvalid={!this.hasValidTimezone()}
                      />
                    </Box>
                  </Flex>
                  <FormErrorMessage fontWeight="regular" marginLeft="100px" px="sp8">
                    {this.props.t("Please select a valid timezone")}
                  </FormErrorMessage>
                </FormControl>
              </Flex>
            </Box>

            {this.state.isSupportAdmin && (
              <Box>
                <SectionHeading title={this.props.t("Multi-Device Management")} icon={this.supportAdminTag()} />

                <Flex px="sp12" py="sp16" direction="column" gap="sp16" maxWidth="776px">
                  <FormControl isDisabled={this.state.parentDevice || this.isDisabled()}>
                    <Checkbox
                      id="isParentDevice"
                      name="isParentDevice"
                      isChecked={this.state.isParentDevice}
                      onChange={this.handle_ChangeIsParent}
                      isDisabled={this.state.parentDevice || this.isDisabled()}
                      data-cy="device-options-is-parent-checkbox"
                    >
                      {this.props.t("Is Parent")}
                    </Checkbox>
                  </FormControl>

                  <FormControl isDisabled={this.state.isParentDevice || this.isDisabled()}>
                    <Flex gap="sp8" alignItems="center">
                      <FormLabel width="100px" htmlFor="parentDevice-input">
                        {this.props.t("Parent Device")}
                      </FormLabel>
                      <Box flexGrow={1} css={this.singleSelectorCss()}>
                        <Select
                          id="parentDevice"
                          placeholder={this.props.t("Select a device...")}
                          items={this.get_DeviceSelectionMap(this.state.timezone)}
                          selectedItem={this.get_DeviceSelectionMap().find((o) => o.value === this.state.parentDevice) ?? { value: "" }}
                          onChange={this.handle_changeParentDevice}
                          disabled={this.state.isParentDevice || this.isDisabled()}
                          required={true}
                          isFiltered={true}
                        />
                      </Box>
                    </Flex>
                  </FormControl>
                </Flex>
              </Box>
            )}

            {this.state.isSupportAdmin && (
              <Box>
                <SectionHeading title={this.props.t("Agent")} icon={this.supportAdminTag()} />

                <Flex px="sp12" py="sp16" direction="column" gap="sp16">
                  <FormControl isDisabled={this.isDisabled()}>
                    <Checkbox
                      id="enableExtensionChromebooksOnly"
                      name="enableExtensionChromebooksOnly"
                      isChecked={this.state.enableExtensionChromebooksOnly}
                      isDisabled={this.isDisabled()}
                      onChange={this.handle_ChangeEnableExtensionChromebooksOnly}
                      data-cy="device-options-enable-extension-checkbox"
                    >
                      {this.props.t("Enable Browser Extension for Chromebooks Only")}
                    </Checkbox>
                  </FormControl>

                  <FormControl isDisabled={this.isDisabled()}>
                    <Checkbox
                      id="studentAutoEnrolment"
                      name="studentAutoEnrolment"
                      isChecked={this.state.studentAutoEnrolment}
                      isDisabled={this.isDisabled()}
                      onChange={this.handle_ChangeStudentAutoEnrolment}
                      data-cy="device-options-student-enrolment-checkbox"
                    >
                      {this.props.t("Student Auto Enrolment")}
                    </Checkbox>
                  </FormControl>
                </Flex>
              </Box>
            )}

            {this.state.isSupportAdmin && (
              <Box>
                <SectionHeading title={this.props.t("Advanced Configuration")} icon={this.supportAdminTag()} />

                <Flex px="sp12" py="sp16" direction="column" gap="sp16" maxWidth="776px">
                  <FormControl isDisabled={this.isDisabled()}>
                    <Flex gap="sp8" alignItems="center">
                      <FormLabel width="120px" htmlFor="customerInternalCompany-input">
                        {this.props.t("Internal Company of Customer")}
                      </FormLabel>
                      <Box flexGrow={1} css={this.singleSelectorCss()}>
                        <Select
                          id="customerInternalCompany"
                          placeholder={this.props.t("Select a customer type...")}
                          items={this.customer_internal_company_mapping}
                          selectedItem={
                            this.customer_internal_company_mapping.find((o) => o.value === this.state.customer_internal_company) ?? {
                              value: "",
                            }
                          }
                          onChange={this.handle_ChangeCustomerType}
                          disabled={this.isDisabled()}
                        />
                      </Box>
                    </Flex>
                  </FormControl>

                  <FormControl isDisabled={this.isDisabled()}>
                    <Flex gap="sp8" alignItems="center">
                      <FormLabel width="120px" htmlFor="hubspotCompanyId">
                        {this.props.t("QID")}
                      </FormLabel>
                      <Box flexGrow={1}>
                        <Input
                          id="hubspotCompanyId"
                          value={this.state.hubspot_company_id}
                          onChange={this.handle_ChangeHubspotCompanyId}
                          isDisabled={this.isDisabled()}
                        />
                      </Box>
                    </Flex>
                  </FormControl>
                </Flex>
              </Box>
            )}

            {this.state.isSupportAdmin && Object.keys(this.state.cpu_info).length > 0 && (
              <Box>
                <SectionHeading title={this.props.t("Appliance CPU Information")} icon={this.supportAdminTag()} />

                <Flex px="sp12" py="sp16" direction="column" gap="sp16">
                  <Box>
                    <table style={{ fontSize: "inherit" }}>
                      <tbody>
                        {Object.entries(this.state.cpu_info).map(([k, v]) => (
                          <tr key={k}>
                            <td style={{ padding: "0.5em 0", paddingRight: "1em" }}>{k}</td>
                            <td style={{ padding: "0.5em 0", wordBreak: "break-all" }}>{v}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Flex>
              </Box>
            )}

            {this.state.isSupportAdmin && Object.keys(this.state.dmi_info).length > 0 && (
              <Box>
                <SectionHeading title={this.props.t("Appliance DMI Information")} icon={this.supportAdminTag()} />

                <Flex px="sp12" py="sp16" direction="column" gap="sp16">
                  <Box>
                    <table style={{ fontSize: "inherit" }}>
                      <tbody>
                        {Object.entries(this.state.dmi_info).map(([k, v]) => (
                          <tr key={k}>
                            <td style={{ padding: "0.5em 0", paddingRight: "1em" }}>{k}</td>
                            <td style={{ padding: "0.5em 0", wordBreak: "break-all" }}>{v}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Flex>
              </Box>
            )}
          </Flex>
        </Box>

        <Box p="sp24" backgroundColor="brand.100" borderBottomRadius="sm">
          <Button variant="primary" disabled={!this.canSave()} onClick={this.handle_Submit}>
            {this.state.saving ? this.props.t("Saving..") : this.state.changes ? this.props.t("Save") : this.props.t("Saved")}
          </Button>
        </Box>
      </CardBasedPage>
    );
  }
}

export default withTranslation()(withToast(ManageDeviceSettings));
